'use client';

import InlineStyle from '@/components/InlineStyle';
import Link from 'next/link';
import { motion } from 'framer-motion';
import BookDemoSection from '@/components/BookDemoSection';
import { BookDemoButton, Button, FaqAccordion, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import PlatformHeroPanel from '@/components/platform/PlatformHeroPanel';
import { slideInLeft, slideInRight, scaleIn, viewportOnce, spring, springSoft } from '@/lib/motion';
import { useTranslation } from '@/hooks/useTranslation';
import {
  Calendar,
  Shield,
  BarChart3,
  Users,
  Activity,
  TrendingUp,
  Sparkles,
  CheckCircle2,
  Rocket,
  Check,
} from 'lucide-react';

/* metadata in ./layout.js — this page is a client component for i18n + motion. */

/* Tints — all derived from the brand sky-blue (#0098FF) family. */
const TINTS = {
  blue:   { bg: '#EAF6FF', stroke: '#BFE3FF', accent: '#0098FF' },
  sky:    { bg: '#EAF3FF', stroke: '#C7DEFF', accent: '#2E90FF' },
  violet: { bg: '#E9EFFF', stroke: '#C6D6FF', accent: '#0057D4' },
  ice:    { bg: '#F2F9FF', stroke: '#D7EEFF', accent: '#5AB4FF' },
};

/* ── Deep-dive background SVGs ──────────────────────────────────────────── */

function CalendarGridBg({ tint }) {
  const startX = 60;
  const cellW = 40;
  const cellH = 24;
  const gap = 4;
  const rows = 3;
  const cols = 7;
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.55 }} aria-hidden="true">
      {Array.from({ length: cols }).map((_, c) => (
        <rect key={`h-${c}`} x={startX + c * (cellW + gap)} y="278" width={cellW} height="4" rx="2" fill={tint.accent} fillOpacity="0.30" />
      ))}
      {Array.from({ length: rows }).map((_, r) =>
        Array.from({ length: cols }).map((_, c) => {
          const isFeatured = r === 1 && c === 3;
          return (
            <rect
              key={`c-${r}-${c}`}
              x={startX + c * (cellW + gap)} y={292 + r * (cellH + gap)}
              width={cellW} height={cellH} rx="5"
              fill={isFeatured ? tint.accent : '#fff'}
              fillOpacity={isFeatured ? 0.85 : 1}
              stroke={tint.accent}
              strokeOpacity={isFeatured ? 0.7 : 0.35}
              strokeWidth={isFeatured ? 1.5 : 1}
            />
          );
        }),
      )}
      <circle cx={startX + 1 * (cellW + gap) + 8} cy={292 + 0 * (cellH + gap) + 8} r="2.5" fill={tint.accent} fillOpacity="0.6" />
      <circle cx={startX + 5 * (cellW + gap) + 8} cy={292 + 0 * (cellH + gap) + 8} r="2.5" fill={tint.accent} fillOpacity="0.6" />
      <circle cx={startX + 2 * (cellW + gap) + 8} cy={292 + 2 * (cellH + gap) + 8} r="2.5" fill={tint.accent} fillOpacity="0.6" />
    </svg>
  );
}

function SentimentTrendBg({ tint }) {
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.55 }} aria-hidden="true">
      <defs>
        <linearGradient id={`st-area-${tint.accent.slice(1)}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={tint.accent} stopOpacity="0.35" />
          <stop offset="100%" stopColor={tint.accent} stopOpacity="0" />
        </linearGradient>
      </defs>
      <line x1="40" y1="360" x2="360" y2="360" stroke={tint.accent} strokeOpacity="0.25" strokeWidth="1" />
      <line x1="40" y1="330" x2="360" y2="330" stroke={tint.accent} strokeOpacity="0.15" strokeDasharray="3 4" strokeWidth="1" />
      <path d="M 40 345 L 100 340 L 160 325 L 220 320 L 280 295 L 340 280 L 340 360 L 40 360 Z" fill={`url(#st-area-${tint.accent.slice(1)})`} />
      <path d="M 40 345 L 100 340 L 160 325 L 220 320 L 280 295 L 340 280" fill="none" stroke={tint.accent} strokeOpacity="0.8" strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx="340" cy="280" r="5" fill={tint.accent} />
      <circle cx="340" cy="280" r="10" fill="none" stroke={tint.accent} strokeOpacity="0.35" strokeWidth="1.2" />
    </svg>
  );
}

function PlatformBarsBg({ tint }) {
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.55 }} aria-hidden="true">
      <line x1="50" y1="360" x2="350" y2="360" stroke={tint.accent} strokeOpacity="0.30" strokeWidth="1.2" />
      {[
        { x: 70,  h1: 80,  h2: 50 },
        { x: 175, h1: 105, h2: 75 },
        { x: 280, h1: 65,  h2: 90 },
      ].map((g, i) => (
        <g key={i}>
          <rect x={g.x}      y={360 - g.h1} width="28" height={g.h1} rx="5" fill={tint.accent} fillOpacity="0.85" />
          <rect x={g.x + 32} y={360 - g.h2} width="28" height={g.h2} rx="5" fill={tint.accent} fillOpacity="0.35" />
        </g>
      ))}
    </svg>
  );
}

function ApprovalFlowBg({ tint }) {
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.55 }} aria-hidden="true">
      <rect x="40" y="310" width="96" height="38" rx="19" fill="#fff" stroke={tint.accent} strokeOpacity="0.4" />
      <circle cx="58" cy="329" r="6" fill={tint.accent} fillOpacity="0.35" />
      <line x1="72" y1="324" x2="120" y2="324" stroke={tint.accent} strokeOpacity="0.5" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="72" y1="334" x2="108" y2="334" stroke={tint.accent} strokeOpacity="0.25" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="140" y1="329" x2="158" y2="329" stroke={tint.accent} strokeOpacity="0.55" strokeWidth="1.5" />
      <path d="M 154 325 L 159 329 L 154 333" fill="none" stroke={tint.accent} strokeOpacity="0.55" strokeWidth="1.5" strokeLinejoin="round" />
      <rect x="160" y="310" width="96" height="38" rx="19" fill={tint.accent} fillOpacity="0.18" stroke={tint.accent} strokeOpacity="0.45" />
      <circle cx="178" cy="329" r="6" fill={tint.accent} fillOpacity="0.65" />
      <line x1="192" y1="324" x2="240" y2="324" stroke={tint.accent} strokeOpacity="0.7" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="192" y1="334" x2="228" y2="334" stroke={tint.accent} strokeOpacity="0.4" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="260" y1="329" x2="278" y2="329" stroke={tint.accent} strokeOpacity="0.55" strokeWidth="1.5" />
      <path d="M 274 325 L 279 329 L 274 333" fill="none" stroke={tint.accent} strokeOpacity="0.55" strokeWidth="1.5" strokeLinejoin="round" />
      <rect x="280" y="310" width="80" height="38" rx="19" fill={tint.accent} fillOpacity="0.85" />
      <circle cx="298" cy="329" r="9" fill="#fff" />
      <path d="M 293 329 L 297 333 L 304 325" fill="none" stroke={tint.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="312" y1="324" x2="350" y2="324" stroke="#fff" strokeOpacity="0.85" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="312" y1="334" x2="342" y2="334" stroke="#fff" strokeOpacity="0.6" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function BoostUpBg({ tint }) {
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.55 }} aria-hidden="true">
      <defs>
        <linearGradient id={`bu-area-${tint.accent.slice(1)}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={tint.accent} stopOpacity="0.45" />
          <stop offset="100%" stopColor={tint.accent} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <line x1="40" y1="360" x2="360" y2="360" stroke={tint.accent} strokeOpacity="0.25" strokeWidth="1" />
      <path d="M 40 340 L 90 335 L 140 338 L 190 330 L 240 326 L 290 318 L 340 285 L 340 360 L 40 360 Z" fill={`url(#bu-area-${tint.accent.slice(1)})`} />
      <path d="M 40 340 L 90 335 L 140 338 L 190 330 L 240 326 L 290 318 L 340 285" fill="none" stroke={tint.accent} strokeOpacity="0.8" strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
      <line x1="340" y1="318" x2="340" y2="290" stroke={tint.accent} strokeOpacity="0.7" strokeWidth="2" strokeLinecap="round" />
      <path d="M 333 296 L 340 288 L 347 296" fill="none" stroke={tint.accent} strokeOpacity="0.85" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx="340" cy="285" r="5" fill={tint.accent} />
    </svg>
  );
}

/* ── Content ────────────────────────────────────────────────────────────── */
const HERO = {
  eyebrow: 'Platform Capability',
  title: 'Social Media Management',
  tagline: 'Unify Social Media Planning, Publishing, and Performance in One Intelligent Platform',
  body: [
    'Locobuzz helps you manage brand conversations, content calendars, and creative workflows across every major channel — so your social presence stays sharp, consistent, and always one step ahead.',
    'Empower marketing and CX teams to schedule, publish, and engage across every digital touchpoint from a single AI-driven inbox. Streamline workflows, ensure brand consistency, and drive measurable ROI through automated, scalable social engagement.',
  ],
};

/* DRAFT — review: impact metrics for Social Media Management; validate numbers with product/marketing. */
const IMPACT = [
  { value: '10+',  label: 'Social channels managed in one inbox' },
  { value: '60%',  label: 'Reduction in publishing turnaround time' },
  { value: '3×',   label: 'Faster content approval with AI workflows' },
  { value: '40%',  label: 'Lift in engagement with optimized scheduling' },
];

const STACK_POINTS = [
  { Icon: Calendar,   title: 'One Smart Calendar',    text: 'Plan, schedule, and publish across channels from one smart calendar.',         tint: 'blue' },
  { Icon: Shield,     title: 'Brand Governance',       text: 'Maintain brand tone and consistency with rule-based governance.',               tint: 'violet' },
  { Icon: BarChart3,  title: 'Performance Insights',   text: 'Measure post-performance and uncover actionable engagement insights.',           tint: 'sky' },
  { Icon: Users,      title: 'Team Collaboration',     text: 'Collaborate seamlessly across teams with built-in workflows and approvals.',     tint: 'blue' },
  { Icon: Activity,   title: 'SignalSense™ Sentiment', text: 'Detect sentiment shifts and emotional tone using SignalSense™.',                 tint: 'violet' },
  { Icon: TrendingUp, title: 'Real-Time Optimization', text: 'Optimize content and boost top performers in real time to drive ROI.',           tint: 'sky' },
];

const DEEP_DIVES = [
  {
    eyebrow: 'Plan & Publish',
    title: 'An Editorial Calendar That Does It All',
    lead: 'Centralize publishing, streamline teamwork, and automate multichannel rollout.',
    bullets: [
      'Manage campaigns, channels, and formats from one calendar',
      'Cross-publish with ease while staying compliant with brand rules',
      'Collaborate with teams through smart workflows and approval layers',
      'Eliminate overlaps with conflict alerts and automated scheduling',
    ],
    cta: { label: 'See the Calendar', href: '#book-demo' },
    visual: { Icon: Calendar, title: 'Editorial Calendar', subtitle: 'Plan, schedule, publish', Bg: CalendarGridBg, tint: 'blue' },
  },
  {
    eyebrow: 'Sentiment Strategy',
    title: 'Use Sentiment to Steer Strategy',
    lead: 'Understand how your audience feels about your content and brand at scale.',
    bullets: [
      'Track tone using SignalSense™ across posts, comments, and hashtags',
      'Spot patterns like praise, backlash, sarcasm — and what’s driving them',
      'Visualize sentiment trends to identify what’s working or broken',
      'Turn emotional insights into actionable strategy across teams',
    ],
    visual: { Icon: Activity, title: 'Sentiment Trend', subtitle: 'SignalSense™ in action', Bg: SentimentTrendBg, tint: 'violet' },
    reverse: true,
    dark: true,
  },
  {
    eyebrow: 'Performance',
    title: 'Track What’s Working and What’s Worth Promoting',
    lead: 'Measure brand performance with clarity, context, and cross-platform consistency.',
    bullets: [
      'Compare post and campaign metrics across platforms and profiles',
      'Auto-tag content by theme, product, or region for smarter insights',
      'Detect high-performing posts and amplify them with precision',
      'Refine strategy with trend-backed insights on engagement and traction',
    ],
    cta: { label: 'Book a Demo', href: '#book-demo' },
    visual: { Icon: BarChart3, title: 'Cross-Platform Metrics', subtitle: 'You vs. category, by channel', Bg: PlatformBarsBg, tint: 'sky' },
  },
  {
    eyebrow: 'Collaboration',
    title: 'Streamline Collaboration Across Teams and Stakeholders',
    lead: 'Workflows that remove chaos and add clarity.',
    bullets: [
      'Set up custom approval flows to streamline reviews and reduce errors',
      'Assign permissions by team or region without losing oversight',
      'Automate checks and maintain consistency across content',
      'Keep everyone aligned — from brand to legal to creative — at every step',
    ],
    visual: { Icon: CheckCircle2, title: 'Approval Workflow', subtitle: 'Draft → Review → Approved', Bg: ApprovalFlowBg, tint: 'blue' },
    reverse: true,
  },
  {
    eyebrow: 'Reach & ROI',
    title: 'Maximize Reach, Minimize Waste',
    lead: 'Optimize content and spend in real time to get the best from every post.',
    bullets: [
      'Identify high-performing content through live insights',
      'Boost only what works — reduce spend, increase ROI',
      'Adjust timing and formats dynamically to fit audience behavior',
      'Eliminate guesswork with data-led promotion and planning',
    ],
    visual: { Icon: Rocket, title: 'Boost What Works', subtitle: 'Live insights, smart spend', Bg: BoostUpBg, tint: 'violet' },
  },
];

/* DRAFT — review: FAQ copy derived from this page’s content; refine before rollout. */
const FAQS = [
  { q: 'What is social media management?', a: 'Social media management is the end-to-end process of planning, scheduling, publishing, and measuring content across social channels — all from a single platform. Locobuzz unifies every step, from editorial calendar to ROI reporting, so teams move faster and stay on-brand.' },
  { q: 'Which social channels does Locobuzz support?', a: 'Locobuzz connects to all major channels — Instagram, Facebook, X (Twitter), LinkedIn, YouTube, Pinterest, and more — letting you publish, engage, and track performance across platforms from one unified inbox.' },
  { q: 'How does AI help with publishing and engagement?', a: 'Locobuzz applies AI at every layer: smart scheduling based on audience behavior, automated sentiment tracking via SignalSense™, AI-assisted approval workflows, and real-time recommendations to boost high-performing posts before spend is wasted.' },
  { q: 'Can multiple teams collaborate without stepping on each other?', a: 'Yes. Role-based permissions, multi-stage approval flows, and conflict alerts keep every team — brand, legal, regional — aligned and in their lane without slowing down publishing velocity.' },
  { q: 'How quickly can we measure ROI from our social content?', a: 'Locobuzz gives you cross-platform analytics from day one: post-level metrics, campaign comparisons, and engagement trends — so you can identify what’s driving ROI and amplify it in real time.' },
];

/* DRAFT — review: Hero panel for Social Media Management. Validate metrics with product. */
const PANEL = {
  title: 'Publishing & Engagement',
  badge: '10+ channels',
  kpiValue: 2847,
  kpiLabel: 'Posts scheduled this week',
  trend: [8, 12, 10, 18, 22, 19, 28, 25, 34, 41],
  trendDelta: '+38%',
  segments: [
    { label: 'Published',  pct: 62, tone: 'pos' },
    { label: 'Scheduled',  pct: 28, tone: 'neu' },
    { label: 'In Review',  pct: 10, tone: 'brand' },
  ],
  feedLabel: 'Incoming comments & DMs',
  feed: [
    { text: 'Love the new product launch post! When is it available? 🙌', meta: '@priya_m', tag: 'Instagram', tone: 'pos' },
    { text: 'Hey, I sent a DM about my order last week — any update?', meta: '@rahul.k', tag: 'Facebook', tone: 'neu' },
    { text: 'This campaign is everywhere right now — well played', meta: '@brand_watch', tag: 'X', tone: 'pos' },
    { text: 'The checkout page keeps erroring out on mobile 😕', meta: '@user_93', tag: 'Instagram', tone: 'neg' },
    { text: 'Really appreciate the quick turnaround on our feedback', meta: '@anita_sees', tag: 'LinkedIn', tone: 'pos' },
  ],
  chip: 'AgentIQ · auto-routed to agent',
};

/* ── Reusable bits ──────────────────────────────────────────────────────── */
function IconTile({ Icon, tint, size = 'md' }) {
  const T = TINTS[tint];
  const dim = size === 'lg' ? 64 : 48;
  const iconSize = size === 'lg' ? 28 : 22;
  return (
    <div style={{ width: dim, height: dim, borderRadius: 14, background: T.bg, border: `1px solid ${T.stroke}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={iconSize} color={T.accent} strokeWidth={1.8} />
    </div>
  );
}

function VisualCard({ visual }) {
  const { t } = useTranslation();
  const T = TINTS[visual.tint];
  const { Bg, Icon } = visual;
  return (
    <div style={{ minHeight: 340, borderRadius: 'var(--radius-3xl)', padding: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', background: `linear-gradient(135deg, #ffffff 0%, ${T.bg} 100%)`, border: `1px solid ${T.stroke}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,152,255,0.06)', position: 'relative', overflow: 'hidden' }}>
      <Bg tint={T} />
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <IconTile Icon={Icon} tint={visual.tint} size="lg" />
        <div>
          <div style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 24, color: 'var(--text-heading)', lineHeight: 1.2, letterSpacing: '-0.015em' }}>{t(visual.title)}</div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 6 }}>{t(visual.subtitle)}</div>
        </div>
      </div>
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────────────────── */
export default function SocialMediaManagementPage() {
  const { t } = useTranslation();
  return (
    <>
      {/* ── HERO ───────────────────────────────────────────────────────── */}
      <section style={{ background: 'var(--gradient-hero)', position: 'relative', overflow: 'hidden', padding: 'var(--hero-pad)' }}>
        <div aria-hidden="true" className="smm-hero-orbs">
          <span className="smm-orb smm-orb1" />
          <span className="smm-orb smm-orb2" />
        </div>
        <InlineStyle css={`
          .smm-hero-orbs { position:absolute; inset:0; pointer-events:none; z-index:0; overflow:hidden; }
          .smm-orb { position:absolute; border-radius:50%; filter:blur(70px); }
          .smm-orb1 { width:680px; height:680px; top:-220px; left:6%; background:radial-gradient(circle at 40% 40%, rgba(0,152,255,0.22), rgba(0,152,255,0.05) 50%, transparent 72%); animation:smmOrb1 22s ease-in-out infinite; }
          .smm-orb2 { width:520px; height:520px; top:-120px; right:4%; background:radial-gradient(circle at 60% 30%, rgba(0,87,212,0.16), rgba(0,152,255,0.04) 55%, transparent 72%); animation:smmOrb2 26s ease-in-out infinite; }
          @keyframes smmOrb1 { 0%,100%{transform:translate(0,0) scale(1);} 50%{transform:translate(30px,-22px) scale(1.06);} }
          @keyframes smmOrb2 { 0%,100%{transform:translate(0,0) scale(1);} 50%{transform:translate(-26px,26px) scale(1.05);} }
          @media (prefers-reduced-motion: reduce){ .smm-orb{animation:none;} }
        `} />

        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <nav aria-label={t('Social Media Management')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 20, padding: '8px 14px 8px 12px', borderRadius: 999, border: '1px solid var(--border-light)', background: 'var(--surface-page, #f5f7ff)', fontSize: 12.5, letterSpacing: '0.02em' }}>
            <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 0 3px rgba(34,197,94,0.16)' }} />
            <Link href="/platform/" style={{ color: 'var(--text-muted)', fontWeight: 600, textDecoration: 'none' }}>{t('Platform')}</Link>
            <span aria-hidden="true" style={{ color: 'var(--text-muted)', opacity: 0.45, fontWeight: 600 }}>›</span>
            <span style={{ color: 'var(--brand)', fontWeight: 700 }}>{t('Social Media Management')}</span>
          </nav>

          <div className="lb-hero-grid">
            <div>
              <span className="lb-pill" style={{ marginBottom: 16 }}>{t(HERO.eyebrow)}</span>
              <motion.h1
                initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.05 }}
                style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 'var(--type-hero-title)', color: 'var(--text-heading)', lineHeight: 1.08, letterSpacing: '-0.02em', margin: '0 0 14px' }}
              >
                {t('Social Media')} <span className="serif-accent italic">{t('Management')}</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springSoft, delay: 0.12 }}
                style={{ fontSize: 'clamp(17px, 1.6vw, 20px)', color: 'var(--brand)', fontWeight: 600, margin: '0 0 18px', lineHeight: 1.4 }}
              >
                {t(HERO.tagline)}
              </motion.p>
              {HERO.body.map((p, i) => (
                <p key={i} style={{ fontSize: 15.5, color: 'var(--text-body)', lineHeight: 1.75, margin: i === 0 ? '0 0 12px' : '0 0 28px' }}>{t(p)}</p>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springSoft, delay: 0.2 }}
                style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}
              >
                <BookDemoButton label={t('Book a Demo')} />
                <Button href="/platform/" variant="secondary" size="lg">{t('Explore Platform')}</Button>
              </motion.div>
            </div>

            <PlatformHeroPanel {...PANEL} />
          </div>
        </div>
      </section>

      {/* ── DARK IMPACT BAND ───────────────────────────────────────────── */}
      {/* DRAFT — review: validate impact metric values with product/marketing before launch. */}
      <section style={{ padding: 'var(--section-pad)' }}>
        <div className="container">
          <MetricsBand items={IMPACT} columns={4} />
        </div>
      </section>

      {/* ── THE STACK ──────────────────────────────────────────────────── */}
      <section className="lb-section-alt">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={spring} style={{ textAlign: 'center', marginBottom: 48 }}>
            <span className="lb-pill" style={{ marginBottom: 14, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Sparkles size={12} color="var(--brand)" strokeWidth={2.2} />
              {t('The Unified Platform')}
            </span>
            <h2 className="lb-h2" style={{ marginBottom: 14 }}>{t('The Unified Platform That Powers')} <span className="serif-accent italic">{t('Social-First Brands')}</span></h2>
            <p style={{ fontSize: 16, color: 'var(--text-body)', maxWidth: 640, margin: '0 auto', lineHeight: 1.7 }}>{t('Because great social media isn’t just posted — it’s orchestrated.')}</p>
          </motion.div>

          <div className="lb-grid-3">
            {STACK_POINTS.map((p, i) => (
              <motion.div
                key={p.title}
                className="lb-card"
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ ...spring, delay: (i % 3) * 0.08 }}
                whileHover={{ y: -6 }}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <IconTile Icon={p.Icon} tint={p.tint} />
                <h3 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 700, fontSize: 17, color: 'var(--text-heading)', lineHeight: 1.35, margin: '14px 0 8px' }}>{t(p.title)}</h3>
                <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.65, margin: 0 }}>{t(p.text)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DEEP DIVES intro ───────────────────────────────────────────── */}
      <section className="lb-section" style={{ paddingBottom: 8 }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={spring} style={{ textAlign: 'center' }}>
            <span className="lb-pill" style={{ marginBottom: 14 }}>{t('What you get')}</span>
            <h2 className="lb-h2" style={{ marginBottom: 14 }}>{t('The Full-Stack Behind')} <span className="serif-accent italic">{t('Smarter Social Media Management')}</span></h2>
            <p style={{ fontSize: 16, color: 'var(--text-body)', maxWidth: 740, margin: '0 auto', lineHeight: 1.7 }}>{t('From planning to publishing to performance — all built to help your teams move faster, stay on-brand, and maximize engagement at scale.')}</p>
          </motion.div>
        </div>
      </section>

      {DEEP_DIVES.map((d, i) => {
        const tone = d.dark
          ? { eyebrow: '#7FB3FF', title: '#ffffff', lead: 'rgba(255,255,255,0.82)', bullet: 'rgba(255,255,255,0.80)', checkBg: 'rgba(127,179,255,0.16)', checkBorder: 'rgba(127,179,255,0.4)', check: '#7FB3FF' }
          : { eyebrow: 'var(--brand)', title: 'var(--text-heading)', lead: 'var(--text-body)', bullet: 'var(--text-secondary)', checkBg: 'var(--brand-subtle)', checkBorder: '#BDD9FF', check: 'var(--brand)' };
        const bg = d.dark ? 'linear-gradient(160deg, #0D1B3E 0%, #0A1454 100%)' : (i % 2 === 0 ? '#ffffff' : '#F8FAFF');
        return (
          <section key={d.title} style={{ background: bg, padding: '72px 0', position: 'relative', overflow: 'hidden' }}>
            <div className="container">
              <div className="lb-hero-grid" style={{ paddingBottom: 0 }}>
                <motion.div variants={d.reverse ? slideInRight : slideInLeft} initial="hidden" whileInView="show" viewport={viewportOnce} style={{ order: d.reverse ? 2 : 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: tone.eyebrow, letterSpacing: '1.8px', textTransform: 'uppercase', marginBottom: 12 }}>{t(d.eyebrow)}</div>
                  <h3 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 'clamp(24px, 3vw, 34px)', color: tone.title, lineHeight: 1.18, letterSpacing: '-0.015em', margin: '0 0 14px' }}>{t(d.title)}</h3>
                  <p style={{ fontSize: 15.5, color: tone.lead, lineHeight: 1.75, margin: '0 0 24px' }}>{t(d.lead)}</p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {d.bullets.map((b, j) => (
                      <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <span aria-hidden="true" style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', background: tone.checkBg, border: `1.5px solid ${tone.checkBorder}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: 3 }}>
                          <Check size={11} color={tone.check} strokeWidth={3} />
                        </span>
                        <span style={{ fontSize: 15, color: tone.bullet, lineHeight: 1.65 }}>{t(b)}</span>
                      </li>
                    ))}
                  </ul>
                  {d.cta && (
                    <div style={{ marginTop: 28 }}>
                      <Button href={d.cta.href} variant="cta" size="lg">{t(d.cta.label)}</Button>
                    </div>
                  )}
                </motion.div>

                <motion.div variants={scaleIn} initial="hidden" whileInView="show" viewport={viewportOnce} style={{ order: d.reverse ? 1 : 2 }}>
                  <VisualCard visual={d.visual} />
                </motion.div>
              </div>
            </div>
          </section>
        );
      })}

      {/* ── SOCIAL PROOF ───────────────────────────────────────────────── */}

      {/* ── FAQ ────────────────────────────────────────────────────────── */}
      {/* DRAFT — review: FAQ copy below derived from page content; refine before rollout. */}
      <PageSection id="faqs" bg="tint">
        <SectionTitle
          title={<>{t('faq.everything_you_need_to')}{' '}<span className="serif-accent italic">{t('faq.know')}</span></>}
          align="center"
        />
        <FaqAccordion items={FAQS.map((f) => ({ q: t(f.q), a: t(f.a) }))} columns={2} name="social-media-management-faq" />
      </PageSection>

      <BookDemoSection />
    </>
  );
}
